import { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Keeps the list and detail queries of one resource consistent after a create, update or
 * delete — whether the change comes from our own mutation or from an RTM event.
 *
 * Every operation follows the same rules:
 * - Only caches that already exist are patched. A list that was never fetched is left alone,
 *   so a partial list can't be seeded and then served as fresh for `staleTime`.
 * - `created` is idempotent: an item already in the list is merged in place, not duplicated.
 * - `removed` drops the item from the list and removes its detail query.
 */

export type EntityChange<TEntity> =
  | { type: 'created'; entity: TEntity; parentId?: string }
  | { type: 'updated'; entity: TEntity; parentId?: string; previousId?: string }
  | { type: 'removed'; id: string; parentId?: string };

export type EntityCacheConfig<TEntity, TListField extends string> = {
  /** Key of the list query. Root resources (brains, desks, …) ignore `parentId`. */
  listKey: (parentId: string | undefined) => QueryKey;
  detailKey: (parentId: string | undefined, id: string) => QueryKey;
  /** Property of the list response that holds the items, e.g. `'brains'` for `{ brains: [] }` */
  listField: TListField;
  getId: (entity: TEntity) => string;
  /** Omit for root resources. */
  getParentId?: (entity: TEntity) => string;
  /** Where `created` inserts a new item. Defaults to `'end'`. */
  insertAt?: 'start' | 'end';
  /** Side effects on other resources, e.g. invalidating a derived query. */
  afterChange?: (queryClient: QueryClient, change: EntityChange<TEntity>) => void;
};

export type EntityCache<TEntity> = {
  created: (queryClient: QueryClient, entity: TEntity, context?: { parentId?: string }) => void;
  /** Pass `previousId` when the id itself changed, e.g. renaming a name-keyed intent. */
  updated: (
    queryClient: QueryClient,
    entity: TEntity,
    context?: { parentId?: string; previousId?: string }
  ) => void;
  removed: (queryClient: QueryClient, target: { id: string; parentId?: string }) => void;
};

type ListResponse<TEntity, TListField extends string> = Record<TListField, TEntity[]>;

export const createEntityCache = <TEntity extends object, TListField extends string>(
  config: EntityCacheConfig<TEntity, TListField>
): EntityCache<TEntity> => {
  const {
    listKey,
    detailKey,
    listField,
    getId,
    getParentId,
    insertAt = 'end',
    afterChange,
  } = config;

  const resolveParentId = (entity: TEntity, parentId?: string) => parentId ?? getParentId?.(entity);

  // Returning undefined from a setQueryData updater is a no-op, which is what keeps
  // uncached queries uncached.
  const patchList = (
    queryClient: QueryClient,
    parentId: string | undefined,
    patch: (items: TEntity[]) => TEntity[]
  ) => {
    queryClient.setQueryData<ListResponse<TEntity, TListField>>(listKey(parentId), (prev) =>
      prev ? { ...prev, [listField]: patch(prev[listField] ?? []) } : undefined
    );
  };

  const mergeDetail = (
    queryClient: QueryClient,
    parentId: string | undefined,
    id: string,
    entity: TEntity
  ) => {
    queryClient.setQueryData<TEntity>(detailKey(parentId, id), (prev) =>
      prev ? { ...prev, ...entity } : undefined
    );
  };

  const created: EntityCache<TEntity>['created'] = (queryClient, entity, context) => {
    const parentId = resolveParentId(entity, context?.parentId);
    const id = getId(entity);

    patchList(queryClient, parentId, (items) => {
      if (items.some((item) => getId(item) === id)) {
        return items.map((item) => (getId(item) === id ? { ...item, ...entity } : item));
      }
      return insertAt === 'start' ? [entity, ...items] : [...items, entity];
    });
    mergeDetail(queryClient, parentId, id, entity);

    afterChange?.(queryClient, { type: 'created', entity, parentId });
  };

  const updated: EntityCache<TEntity>['updated'] = (queryClient, entity, context) => {
    const parentId = resolveParentId(entity, context?.parentId);
    const id = getId(entity);
    const previousId = context?.previousId ?? id;

    patchList(queryClient, parentId, (items) =>
      items.map((item) => (getId(item) === previousId ? { ...item, ...entity } : item))
    );

    if (previousId === id) {
      mergeDetail(queryClient, parentId, id, entity);
    } else {
      // The id changed: copy the cached detail to its new key. The old key stays, updated, because
      // a page can still be mounted on it (until it navigates, or for a rename from another
      // session); garbage collection drops it once nothing observes it.
      const previous = queryClient.getQueryData<TEntity>(detailKey(parentId, previousId));
      if (previous) {
        queryClient.setQueryData<TEntity>(detailKey(parentId, id), { ...previous, ...entity });
      }
      mergeDetail(queryClient, parentId, previousId, entity);
    }

    afterChange?.(queryClient, { type: 'updated', entity, parentId, previousId });
  };

  const removed: EntityCache<TEntity>['removed'] = (queryClient, { id, parentId }) => {
    patchList(queryClient, parentId, (items) => items.filter((item) => getId(item) !== id));
    queryClient.removeQueries({ queryKey: detailKey(parentId, id) });

    afterChange?.(queryClient, { type: 'removed', id, parentId });
  };

  return { created, updated, removed };
};
